// ============================================
// PartnerDashboard — Panel exclusivo de socios distribuidores (/socio)
// Layout standalone. Navegación: dropdown superior "Mi panel ▾".
// Si llega un cliente NO distribuidor, se le redirige al panel normal.
// ============================================

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountInfo from '../components/account/AccountInfo';
import OrderHistory from '../components/account/OrderHistory';
import Returns from '../components/account/Returns';
import Communications from '../components/account/Communications';
import LoginModal from '../components/account/LoginModal';
import DistributorProgram from '../components/account/DistributorProgram';
import PointsPanel from '../components/account/PointsPanel';
import LoadingDots from '../components/shop/LoadingDots';
import ConfirmModal from '../components/ConfirmModal';
import { useCustomer } from 'context/CustomerContext';
import { getMyDistributorStatus } from 'services/medusa/distributor';
import SEO from '../components/SEO';
import '../pages/Account.css';

const PartnerDashboard = () => {
  const {
    customer,
    isAuthenticated,
    loading,
    logout,
    updateCustomer,
    refreshCustomer,
    token,
  } = useCustomer();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('partner-panel');
  const [showLogin, setShowLogin] = useState(false);
  const [distStatus, setDistStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setStatusLoading(false);
      return;
    }
    let cancelled = false;
    setStatusLoading(true);
    getMyDistributorStatus(token)
      .then((s) => {
        if (!cancelled) setDistStatus(s);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const isDistributor = Boolean(distStatus?.is_distributor);
  // Cliente (no socio) autenticado: en vez de redirigir en silencio, mostramos
  // un aviso de que el panel es exclusivo para socios.
  const showNotPartner = !loading && !statusLoading && isAuthenticated && !isDistributor;

  const menuOptions = [
    { id: 'partner-panel', label: 'Mi panel' },
    { id: 'points', label: 'Puntos' },
    { id: 'account-info', label: 'Mi cuenta' },
    { id: 'order-history', label: 'Historial de pedidos' },
    { id: 'returns', label: 'Devoluciones' },
    { id: 'communications', label: 'Comunicaciones' },
  ];

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const handleLogout = () => setShowLogoutConfirm(true);
  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    window.location.href = '/';
  };

  const handleMenuSelect = (sectionId) => {
    setActiveSection(sectionId);
  };

  const getCustomerName = () => {
    if (!customer) return '';
    const fn = customer.first_name || '';
    const ln = customer.last_name || '';
    const joined = `${fn} ${ln}`.trim();
    return joined || customer.email || '';
  };

  const socioId =
    customer?.metadata?.distributor_id ||
    (distStatus?.application?.id
      ? String(distStatus.application.id).slice(-6).toUpperCase()
      : customer?.id
      ? String(customer.id).slice(-6).toUpperCase()
      : '——————');

  const renderSection = () => {
    switch (activeSection) {
      case 'partner-panel':
        return <DistributorProgram />;
      case 'points':
        return <PointsPanel socioId={socioId} />;
      case 'account-info':
        return (
          <AccountInfo
            customer={customer}
            updateCustomer={updateCustomer}
            refreshCustomer={refreshCustomer}
          />
        );
      case 'order-history':
        return <OrderHistory />;
      case 'returns':
        return <Returns />;
      case 'communications':
        return (
          <Communications customer={customer} updateCustomer={updateCustomer} />
        );
      default:
        return <DistributorProgram />;
    }
  };

  if (loading || statusLoading) {
    return (
      <div className="partner-shell" data-header-theme="light">
        <div className="partner-shell__loading">
          <LoadingDots overlay={false} />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="partner-shell" data-header-theme="light">
          <div className="partner-shell__login">
            <div
              className="partner-shell__login-cover"
              style={{
                backgroundImage:
                  "url('/images/secciones/Socios/Portada_Inicio_pagina_socios.webp')",
              }}
              aria-hidden="true"
            />
            <div className="partner-shell__login-panel">
              <h1>Panel de socio</h1>
              <p>Inicia sesión con tu cuenta de socio.</p>
              <button
                className="partner-shell__login-btn"
                onClick={() => setShowLogin(true)}
              >
                Iniciar sesión
              </button>
              <p className="partner-shell__login-note">
                Este panel es <strong>exclusivo para socios Physiology</strong>.
                Si tienes una cuenta de cliente, ingresa desde{' '}
                <a href="/account">tu cuenta</a>.
              </p>
              <p className="partner-shell__login-become">
                ¿Quieres ser socio Physiology?{' '}
                <a href="/socios-y-distribuidores">Conoce cómo unirte</a>
              </p>
            </div>
          </div>
        </div>
        <LoginModal
          isOpen={showLogin}
          onClose={() => setShowLogin(false)}
          onLoginSuccess={() => setShowLogin(false)}
        />
      </>
    );
  }

  if (showNotPartner) {
    return (
      <div className="partner-shell" data-header-theme="light">
        <div className="partner-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="partner-modal-title">
          <div className="partner-modal">
            <button
              type="button"
              className="partner-modal__close"
              aria-label="Cerrar"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            >
              ×
            </button>
            <h2 id="partner-modal-title" className="partner-modal__title">Acceso exclusivo para socios</h2>
            <p className="partner-modal__text">
              Tu cuenta es de cliente. El <strong>panel de socio</strong> es exclusivo
              para socios y distribuidores Physiology.
            </p>
            <div className="partner-modal__actions">
              <button
                className="partner-shell__login-btn"
                onClick={() => navigate('/account')}
              >
                Ir a mi cuenta
              </button>
              <a className="partner-modal__link" href="/socios-y-distribuidores">
                ¿Cómo ser socio Physiology?
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const customerName = getCustomerName();

  return (
    <div className="partner-shell" data-header-theme="light">
      <SEO
        title="Panel de socio"
        description="Panel exclusivo para socios distribuidores Physiology"
        noindex
      />

      {/* ─── Hero (greeting + motto) ─────────────────────────────── */}
      <header className="partner-hero">
        <div className="partner-hero__left">
          <p className="partner-hero__greeting">Hola, {customerName}</p>
          <p className="partner-hero__meta">No. de socio: {socioId}</p>
        </div>
        <h1 className="partner-hero__motto">Aquí cuidas a quien cuidas</h1>
      </header>

      {/* ─── Cuerpo: columna de navegación + contenido ───────────── */}
      <div className="partner-body">
        <aside className="partner-sidebar">
          <nav id="partner-nav-menu" className="partner-nav-menu">
            {menuOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`partner-nav-menu__item ${
                  activeSection === option.id ? 'is-active' : ''
                }`}
                onClick={() => handleMenuSelect(option.id)}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              className="partner-nav-menu__item partner-nav-menu__item--logout"
              onClick={handleLogout}
            >
              Cerrar sesión
            </button>
          </nav>
        </aside>

        <main className="partner-shell__content">{renderSection()}</main>
      </div>

      {/* Slot de ancho completo para el carousel del panel (portal desde
          DistributorProgram). Va fuera del grid para que la columna y su
          línea divisoria no lleguen por detrás del carousel. */}
      {activeSection === 'partner-panel' && (
        <div id="partner-carousel-slot" className="partner-carousel-slot" />
      )}

      <ConfirmModal
        isOpen={showLogoutConfirm}
        message="¿Estás seguro que deseas cerrar sesión?"
        confirmText="Cerrar sesión"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </div>
  );
};

export default PartnerDashboard;
